/**
 * The one place a finding's disposition is decided (P3, plan decision D7).
 *
 * Before this module, accepts could only have been applied inside the audit
 * engine — and that would have been silently wrong, because the audit engine
 * sees only its own 19 checks. Preflight findings, link-check findings and the
 * late post-ZIP findings never pass through it, yet they are counted by
 * `buildConfidenceReport` and gated by `enforceExportGate`. An accept that the
 * report ignored would be a waiver the operator could see but not rely on.
 *
 * So every source flows through here instead:
 *
 *   all findings (audit + preflight + link + late)
 *     → normalize identity        attach acceptKey where the check opted in
 *     → apply accepts             MARK, never delete
 *     → compute open/accepted     counts describe what is still open
 *     → evaluate shipping policy  (P8 extends this)
 *     → render UI / report / lock
 *
 * Accepted findings are marked, not dropped. A shipped export must always be
 * able to say what was waived; a report that quietly omits accepted findings is
 * a report that lies by omission.
 *
 * Pure: no imports from state, storage or UI. The caller passes the accept map.
 */

import { CHECK_KIND, SEVERITY } from './audit-checks.js';
import { MAX_ACCEPT_RECORDS, isValidAcceptKey } from './audit-accept-schema.js';

/** Disposition values. Distinct concepts — never collapse them (D6). */
export const DISPOSITION = Object.freeze({
  /** Still open. Counted, shown, and it drives policy. */
  OPEN: 'open',
  /** A durable project-level decision that this finding is fine. */
  ACCEPTED: 'accepted',
  /** Export policy shipped a recoverable defect. It remains an open issue. */
  AUTO_OVERRIDDEN: 'autoOverridden',
  /** A download shipped a NON-recoverable defect (gate v4). Still open. */
  EXPORTED_ANYWAY: 'exportedAnyway',
  /** Publish recognition was recorded for this exact snapshot. */
  PUBLISH_ACKNOWLEDGED: 'publishAcknowledged'
});

/**
 * Normalize the accept map into a lookup of acceptKey → record.
 *
 * State stores accepts under a DIGEST path (see core/asset-accepts), because
 * `setByPath` splits on `.` and a raw key containing a domain or a selector
 * would silently nest into objects. The digest is an implementation detail of
 * storage; matching is always on the key itself.
 */
export function indexAccepts(auditAccepts) {
  const byKey = new Map();
  if (!auditAccepts || typeof auditAccepts !== 'object') return byKey;
  for (const record of Object.values(auditAccepts).slice(0, MAX_ACCEPT_RECORDS)) {
    if (!record || typeof record !== 'object') continue;
    const key = typeof record.key === 'string' ? record.key : null;
    if (!isValidAcceptKey(key)) continue;
    byKey.set(key, record);
  }
  return byKey;
}

/**
 * Apply accepts to a merged finding list.
 *
 * @param {object[]} findings - every source, already merged and deduped by id
 * @param {object} [auditAccepts] - state.auditAccepts
 * @returns {{
 *   findings: object[],          all findings, each carrying `disposition`
 *   open: object[],              disposition === 'open'
 *   accepted: object[],          disposition === 'accepted'
 *   counts: {blocking:number, warning:number, cleanup:number, maintenance:number},  OPEN only
 *   acceptedCounts: {blocking:number, warning:number, cleanup:number, maintenance:number},
 *   allClear: boolean            nothing open
 * }}
 */
export function disposeFindings(findings, auditAccepts = null) {
  const byKey = indexAccepts(auditAccepts);
  const list = Array.isArray(findings) ? findings : [];
  const out = [];
  const open = [];
  const accepted = [];

  for (const finding of list) {
    if (!finding) continue;
    const key = finding.acceptKey || null;
    const record = key ? byKey.get(key) : null;
    // Policy/recognition stamps are applied AFTER the accept lookup. Preserve
    // them when a stamped list passes through this central stage again (the
    // export report does exactly that), otherwise disclosure is silently reset
    // to `open`. They still belong to the open bucket: neither an automatic
    // export policy nor publish recognition is a durable acceptance decision.
    const carried =
      finding.disposition === DISPOSITION.AUTO_OVERRIDDEN ||
      finding.disposition === DISPOSITION.EXPORTED_ANYWAY ||
      finding.disposition === DISPOSITION.PUBLISH_ACKNOWLEDGED
        ? finding.disposition
        : DISPOSITION.OPEN;
    const disposition = record ? DISPOSITION.ACCEPTED : carried;
    const kind = finding.kind || CHECK_KIND[finding.checkId] || 'content';
    const decided = { ...finding, kind, disposition };
    if (record) {
      // Attribution rides the finding so the report can say WHO waived it.
      decided.acceptedBy = record.by || null;
      decided.acceptedNote = record.note || null;
    } else {
      delete decided.acceptedBy;
      delete decided.acceptedNote;
    }
    out.push(decided);
    (record ? accepted : open).push(decided);
  }

  const tally = (rows) => {
    const counts = { blocking: 0, warning: 0, cleanup: 0, maintenance: 0 };
    for (const f of rows) {
      if (f.severity in counts) counts[f.severity] += 1;
      if (f.kind === 'maintenance') counts.maintenance += 1;
    }
    return counts;
  };

  return {
    findings: out,
    open,
    accepted,
    counts: tally(open),
    acceptedCounts: tally(accepted),
    allClear: open.length === 0
  };
}

/**
 * Which findings are still blocking AFTER accepts. This is what a gate must
 * read — reading raw findings would re-raise something the operator already
 * decided, which is the whole failure this phase removes.
 */
export function openBlocking(disposed) {
  return (disposed?.open || []).filter((f) => f && f.severity === SEVERITY.BLOCKING);
}

/**
 * The accept keys an export shipped under, sorted, for the provenance lock.
 *
 * KEYS ONLY — never the record. `at` is wall-clock and `by` is an identity;
 * neither may enter a builder-emitted export artifact (that rule exists because
 * `export-lock-tests.js` proves a lock is byte-identical across two runs of the
 * same state, and a timestamp would break it forever).
 */
export function acceptKeysForLock(auditAccepts) {
  return [...indexAccepts(auditAccepts).keys()].sort();
}
